import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle, Calendar, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

interface CashflowPredictionWidgetProps {
    currentBalance: number;
    monthlyBurnRate: number; // Média de gastos mensais
    incomingRevenue: number; // Recebimentos previstos
}

export const CashflowPredictionWidget = ({
    currentBalance = 0,
    monthlyBurnRate = 0,
    incomingRevenue = 0
}: CashflowPredictionWidgetProps) => {

    // Cálculo simples de projeção (mock da IA por enquanto)
    const projectBalance = (days: number) => {
        const months = days / 30;
        return currentBalance + (incomingRevenue * months) - (monthlyBurnRate * months);
    };

    const projection30 = projectBalance(30);
    const projection60 = projectBalance(60);
    const projection90 = projectBalance(90);

    const getHealthStatus = (balance: number) => {
        if (balance < 0) return { label: "Crítico", color: "text-red-500", bg: "bg-red-500/10" };
        if (balance < monthlyBurnRate) return { label: "Atenção", color: "text-amber-500", bg: "bg-amber-500/10" };
        return { label: "Saudável", color: "text-emerald-500", bg: "bg-emerald-500/10" };
    };

    const status30 = getHealthStatus(projection30);
    const status90 = getHealthStatus(projection90);

    return (
        <Card className="col-span-1 lg:col-span-2 overflow-hidden border-primary/20 bg-gradient-to-br from-card to-primary/5">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary" />
                            Previsão de Fluxo de Caixa (IA)
                        </CardTitle>
                        <CardDescription>
                            Projeção inteligente baseada no histórico
                        </CardDescription>
                    </div>
                    <Badge variant="outline" className="bg-background/50 backdrop-blur-sm border-primary/20 text-primary">
                        <SparklesIcon className="w-3 h-3 mr-1" />
                        Beta
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                    {/* Status Atual */}
                    <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Saldo Disponível</p>
                        <p className="text-2xl font-bold">
                            R$ {currentBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <span className="text-red-500 flex items-center">
                                <TrendingDown className="w-3 h-3 mr-0.5" />
                                -R$ {monthlyBurnRate.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}/mês
                            </span>
                            <span>(média gastos)</span>
                        </div>
                    </div>

                    {/* Projeção 30 Dias */}
                    <div className="relative p-4 rounded-xl bg-background/50 border border-border/50">
                        <div className="absolute top-2 right-2">
                            <Badge variant="secondary" className={`text-[10px] h-5 px-1.5 ${status30.bg} ${status30.color} border-0`}>
                                {status30.label}
                            </Badge>
                        </div>
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            Em 30 dias
                        </p>
                        <p className={`text-xl font-bold mt-1 ${projection30 < 0 ? 'text-destructive' : 'text-foreground'}`}>
                            R$ {projection30.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                        </p>
                        {projection30 < 0 && (
                            <p className="text-[10px] text-destructive mt-1 flex items-center">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Risco de caixa negativo
                            </p>
                        )}
                    </div>

                    {/* Projeção 90 Dias */}
                    <div className="relative p-4 rounded-xl bg-background/50 border border-border/50">
                        <div className="absolute top-2 right-2">
                            <Badge variant="secondary" className={`text-[10px] h-5 px-1.5 ${status90.bg} ${status90.color} border-0`}>
                                {status90.label}
                            </Badge>
                        </div>
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            Em 90 dias
                        </p>
                        <p className={`text-xl font-bold mt-1 ${projection90 < 0 ? 'text-destructive' : 'text-foreground'}`}>
                            R$ {projection90.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                        </p>
                    </div>
                </div>

                {/* AI Insights */}
                <div className="mt-6 p-3 bg-primary/5 rounded-lg border border-primary/10 flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-full shrink-0">
                        <SparklesIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Insight da IA</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {projection90 < projection30
                                ? "Seu ritmo de gastos está maior que as receitas previstas. Considere renegociar prazos de fornecedores."
                                : "Fluxo de caixa saudável. Ótimo momento para antecipar compras de materiais com desconto."
                            }
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 text-xs shrink-0 gap-1">
                        Detalhes
                        <ArrowRight className="w-3 h-3" />
                    </Button>
                </div>

            </CardContent>
        </Card>
    );
};

function SparklesIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
    )
}
